import { ChevronRight } from "@app/ui";
import type { PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "../../lib/motion/index.js";
import { useI18n } from "../../lib/useI18n.js";

interface Props {
  title: string;
  collapsed: boolean;
  onToggle: () => void;
  /** When set, a "Manage" link is shown (revealed on hover/focus) linking here. */
  manageTo?: string;
  /** Optional drag handle rendered at the start of the header (for reordering). */
  grip?: ReactNode;
  /** Optional pointer hook that turns the title text into a drag handle. */
  onTitlePointerDown?: ((e: ReactPointerEvent) => void) | undefined;
  children: ReactNode;
}

/** A collapsible sidebar section: a header row (chevron + title) that toggles the
 *  body, plus an optional "Manage" link that fades in only while the section is
 *  hovered or keyboard-focused (scoped via the named `group/ss`). */
export function SidebarSection({ title, collapsed, onToggle, manageTo, grip, onTitlePointerDown, children }: Props) {
  const { t } = useI18n();
  const reduce = useReducedMotion();
  const titleClass = `text-xs font-medium uppercase tracking-wide text-muted-foreground ${onTitlePointerDown ? "cursor-grab touch-none active:cursor-grabbing" : ""}`;
  return (
    <div className="group/ss flex flex-col">
      <div className="flex items-center gap-1 px-2 py-1">
        {grip}
        {onTitlePointerDown ? (
          <>
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={!collapsed}
              aria-label={t(collapsed ? "sidebar.expand" : "sidebar.collapse", { title })}
              className="flex shrink-0 items-center text-left"
            >
              <ChevronRight className={`h-3 w-3 shrink-0 text-muted-foreground transition-transform ${collapsed ? "" : "rotate-90"}`} />
            </button>
            <span title={t("sidebar.dragSection")} onPointerDown={onTitlePointerDown} className={titleClass}>
              {title}
            </span>
            <span aria-hidden className="min-w-0 flex-1" />
          </>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={!collapsed}
            className="flex flex-1 items-center gap-1 text-left"
          >
            <ChevronRight className={`h-3 w-3 shrink-0 text-muted-foreground transition-transform ${collapsed ? "" : "rotate-90"}`} />
            <span className={titleClass}>{title}</span>
          </button>
        )}
        {manageTo ? (
          <Link
            to={manageTo}
            title={t("sidebar.manageTitle", { title })}
            className="text-xs text-muted-foreground opacity-0 transition-opacity duration-200 hover:text-foreground focus:opacity-100 group-hover/ss:opacity-100"
          >
            {t("sidebar.manage")}
          </Link>
        ) : null}
      </div>
      <AnimatePresence initial={false}>
        {!collapsed ? (
          <motion.div
            key="body"
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.18 }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
